import { useNavigate, useParams } from 'react-router';
import { ArrowLeft, Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useInvestorDrawdownDetail } from '../hooks/useInvestorDrawdowns';
import { DrawdownDetail } from '../components/DrawdownDetail';
import { useDocumentDownload } from '../../documents/hooks/useDocuments';
import { capitalCallNoticeFilename } from '../drawdown-notice-filename';

export function DrawdownDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const {
    data: drawdown,
    isLoading,
    error,
  } = useInvestorDrawdownDetail(id ?? null);
  const { downloadDocument } = useDocumentDownload();

  const handleDownloadNotice = () => {
    if (!drawdown?.notice_document_id) return;

    downloadDocument(
      drawdown.notice_document_id,
      capitalCallNoticeFilename(drawdown.purpose)
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !drawdown) {
    return (
      <div className="space-y-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/portal/transactions')}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Transactions
        </Button>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <h3 className="text-lg font-medium mb-2">Transaction Not Found</h3>
            <p className="text-muted-foreground text-center max-w-md">
              The drawdown you&apos;re looking for could not be found or you
              don&apos;t have access to it.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/portal/transactions')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-2xl font-bold">
          Drawdown #{drawdown.drawdown_number} Details
        </h1>
        {drawdown.notice_document_id && (
          <button
            type="button"
            className="ml-auto flex items-center gap-1 text-sm text-primary hover:text-primary/80 px-2"
            onClick={handleDownloadNotice}
          >
            <Download className="h-4 w-4" />
            <span>PDF</span>
          </button>
        )}
      </div>

      <DrawdownDetail drawdown={drawdown} />
    </div>
  );
}
